/**
 * TierContext
 *
 * Provides subscription tier information to the UI.
 * Fetches from GET /api/v1/stripe/subscription.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useRole } from '@/contexts/RoleContext';

// Re-exported from the canonical Zod-derived type so existing
// `import { SubscriptionTier } from '@/contexts/TierContext'` call sites keep
// working — this used to be its own copy of the same six literals.
import type { SubscriptionTier } from '@aica/api-types/schemas/stripe';
export type { SubscriptionTier };

interface PendingDowngrade {
  tier: SubscriptionTier;
  effectiveAt: string | null;
}

interface TierContextValue {
  tier: SubscriptionTier;
  status: string;
  limits: Record<string, number>;
  usage: {
    usedMinutes: number;
    includedMinutes: number;
    overageMinutes: number;
  };
  isLoading: boolean;
  /**
   * Whether `tier`, `limits` and `usage` describe this org, or are the neutral
   * defaults below.
   *
   * They are defaults in three cases: no `TierProvider` is mounted (the
   * embedded Bitrix24 shell mounts none), the request is skipped for a role
   * without billing.read, or it is still in flight. `isLoading` only covers the
   * third — a consumer that trusted it printed the default 100 included minutes
   * as fact to a Pro org inside Bitrix24. Anything that shows a NUMBER from
   * this context must gate on this flag; a feature gate reading `tier` is fine
   * treating the default as free.
   */
  hasPlanData: boolean;
  /**
   * ISO end of the current billing period — the moment the included-minute
   * grant resets. Null for orgs without a Stripe subscription (free, custom
   * billing), whose free minutes reset on the calendar month instead.
   */
  currentPeriodEnd: string | null;
  /**
   * ISO start of the current billing period. Paired with `currentPeriodEnd` it
   * gives the period's real length, which is what an upgrade proration is
   * measured against — a monthly and an annual plan prorate over very
   * different spans, so neither may be assumed to be 30 days.
   */
  currentPeriodStart: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  pendingDowngrade: PendingDowngrade | null;
  isWithinLimit: (limitKey: string, currentCount: number) => boolean;
}

const defaultValue: TierContextValue = {
  tier: 'free',
  status: 'active',
  limits: {},
  usage: { usedMinutes: 0, includedMinutes: 100, overageMinutes: 0 },
  isLoading: false,
  hasPlanData: false,
  currentPeriodEnd: null,
  currentPeriodStart: null,
  cancelAtPeriodEnd: false,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  pendingDowngrade: null,
  isWithinLimit: () => true,
};

const TierContext = createContext<TierContextValue>(defaultValue);

export function TierProvider({ children }: { children: ReactNode }) {
  const { apiClient } = useApiClient();
  const { canSeeBilling, selectedOrgId } = useRole();

  // RBAC billing.read = client + platform owner. Fetching for other roles
  // (agent, supervisor, admin) is a guaranteed 403 — skip the request and
  // serve the free-tier defaults instead. Org id in the key: the response is
  // org-scoped (_org param), so a global key would leak tier across switches.
  const { data, isLoading } = useQuery({
    queryKey: ['stripe-subscription', selectedOrgId],
    queryFn: () => apiClient.billing.getSubscription(),
    staleTime: 60_000,
    enabled: canSeeBilling && !!selectedOrgId,
  });

  const tier: SubscriptionTier = (data?.tier as SubscriptionTier) || 'free';
  const limits: Record<string, number> = data?.limits || {};

  const value: TierContextValue = {
    tier,
    status: data?.status || 'active',
    limits,
    usage: data?.usage || { usedMinutes: 0, includedMinutes: 100, overageMinutes: 0 },
    isLoading,
    hasPlanData: !!data,
    currentPeriodEnd: data?.currentPeriodEnd ?? null,
    currentPeriodStart: data?.currentPeriodStart ?? null,
    cancelAtPeriodEnd: data?.cancelAtPeriodEnd || false,
    stripeCustomerId: data?.stripeCustomerId ?? null,
    stripeSubscriptionId: data?.stripeSubscriptionId ?? null,
    pendingDowngrade: (data?.pendingDowngrade as PendingDowngrade | null) ?? null,
    isWithinLimit: (limitKey: string, currentCount: number) => {
      const limit = limits[limitKey];
      if (limit === undefined || limit === -1) return true;
      return currentCount < limit;
    },
  };

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
}

export function useTier(): TierContextValue {
  return useContext(TierContext);
}
